/**
 * Seed canónico de los 55 stakeholders del proyecto FAC Colombia.
 *
 * Fuente: `docs/Matriz_Interesados_Agil_orgchart (1).xlsx` (hoja Matriz).
 *
 * Cobertura:
 *   - STK-001..031: Roles internos Tecpetrol (E&P → soporte/operación).
 *   - STK-032..036: Reguladores y gobierno (ANLA, ANH, alcaldías, SPE).
 *   - STK-037..038: Comunidades de área de influencia.
 *   - STK-039..043: Empresarios, operadoras vecinas (Ecopetrol, Frontera).
 *   - STK-044..049: Contratistas y servicios locales (cantera, O&M,
 *     Techint, ingeniería, marco, hotel).
 *   - STK-050..055: Proveedores especialistas y OT (Schneider, Flowserve,
 *     SYZ, IT/OT Tecpetrol, OT manager, OT expert).
 *
 * Se carga vía `ensureSeedStakeholders(tenantId)` la primera vez que se
 * abre el módulo. Idempotente: si ya existen stakeholders para el tenant,
 * no hace nada (no sobreescribe trabajo del usuario).
 *
 * Notas de mapeo:
 *   - Canales compuestos del Excel ("Teams/Correo", "Reunión/Correo")
 *     se mapean a "Mixto".
 *   - Cadencias no estándar: Bimestral → Mensual; Semestral/Anual →
 *     Trimestral; Ad-hoc → "Por hito".
 *   - Los `responsableEmail` son placeholders `nombre.apellido@tecpetrol.com`
 *     derivados del Excel; el PL puede corregirlos en la página de detalle.
 *   - `cuadrante` y `gap` NO se persisten en el seed: el repo los deriva
 *     al crear.
 */

import { buildRepos } from "@core/data/repos";
import { GERENCIA_DEFAULT } from "@constants/gerencias";
import type {
  CadenciaContacto,
  CanalStakeholder,
  NivelEngagement,
} from "@app-types/entities";

interface StakeholderSeedSpec {
  /** Solo informativo — el repo asigna el código secuencial real. */
  codigo: string;
  nombre: string;
  organizacion: string;
  rol: string;
  nivelOrganizacional: 1 | 2 | 3 | 4 | 5 | 6;
  poder: 1 | 2 | 3 | 4 | 5;
  interes: 1 | 2 | 3 | 4 | 5;
  engagementActual: NivelEngagement;
  engagementDeseado: NivelEngagement;
  necesidad: string;
  accionClave: string;
  responsableEmail: string;
  canalPreferido: CanalStakeholder;
  cadencia: CadenciaContacto;
  sentimientoIa?: "POSITIVO" | "NEUTRO" | "NEGATIVO";
  proximaRevision: string;
  estado: "ACTIVO" | "INACTIVO";
}

const STAKEHOLDERS_FAC: StakeholderSeedSpec[] = [
  // ───────────────────────────────────────────────────────────────────
  // Nivel 1 — Dirección ejecutiva
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-001",
    nombre: "Ricardo Miguel Markous",
    organizacion: "Dirección Tecpetrol",
    rol: "CEO",
    nivelOrganizacional: 1,
    poder: 5,
    interes: 2,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Comunicaciones ejecutivas / lineamientos",
    accionClave: "Brief ejecutivo",
    responsableEmail: "jose.feretti@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Trimestral",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-002",
    nombre: "Ricardo Raul Ferreiro",
    organizacion: "Dirección E&P",
    rol: "President E&P",
    nivelOrganizacional: 1,
    poder: 5,
    interes: 2,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Alineación con prioridades E&P",
    accionClave: "Brief ejecutivo",
    responsableEmail: "jose.feretti@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Trimestral",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-003",
    nombre: "Jose Ignacio Feretti",
    organizacion: "Tecpetrol Colombia",
    rol: "Country Director",
    nivelOrganizacional: 1,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Escalamiento de temas críticos",
    accionClave: "Update ejecutivo",
    responsableEmail: "nahuel.allo@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Nivel 2 — Sr. Managers / Sponsors directos
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-004",
    nombre: "Marco Aurelio Hernandez Abuabara",
    organizacion: "Operaciones",
    rol: "Operations Sr. Manager (Cliente directo)",
    nivelOrganizacional: 2,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Aceptación operativa de entregables",
    accionClave: "Revisión operativa + acuerdos",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-005",
    nombre: "Nahuel Allo",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Sr. Manager",
    nivelOrganizacional: 2,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Sponsor/decisión y priorización",
    accionClave: "1:1 + Steering",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Quincenal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-006",
    nombre: "Guillermo Ariel Rubio Murcia",
    organizacion: "Operaciones",
    rol: "Operaciones (Cliente directo)",
    nivelOrganizacional: 2,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Alineación operativa",
    accionClave: "Reunión focal",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-007",
    nombre: "Nestor Javier Contreras Pinto",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Manager",
    nivelOrganizacional: 2,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Gestión diaria del proyecto",
    accionClave: "Seguimiento semanal",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-008",
    nombre: "Nicolas Raimundo Militello",
    organizacion: "Abastecimiento",
    rol: "Procurement Sr. Manager",
    nivelOrganizacional: 2,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "PO/Compras y contratación",
    accionClave: "Mesa compras",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-009",
    nombre: "Juan Pablo Presser",
    organizacion: "HSE",
    rol: "HSE Sr. Manager",
    nivelOrganizacional: 2,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Permisos y HSE",
    accionClave: "Revisión permisos",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-010",
    nombre: "Karina Valero Gongora",
    organizacion: "RRHH",
    rol: "HRBP Manager",
    nivelOrganizacional: 2,
    poder: 3,
    interes: 3,
    engagementActual: "NEUTRAL",
    engagementDeseado: "NEUTRAL",
    necesidad: "Soporte RRHH",
    accionClave: "Comunicación puntual",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Nivel 3 — Managers / Lead Supervisors
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-011",
    nombre: "Nelson Camilo Cabrejo Hernandez",
    organizacion: "RRLL",
    rol: "Labor Relations Manager",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 3,
    engagementActual: "NEUTRAL",
    engagementDeseado: "NEUTRAL",
    necesidad: "RRLL y socio-laboral",
    accionClave: "Revisión RRLL",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-012",
    nombre: "Catalina Echeverri Gonzalez",
    organizacion: "CORE",
    rol: "CORE Manager",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Soporte CORE y coordinación",
    accionClave: "Mesa de trabajo",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-013",
    nombre: "Rodrigo Nahuel Alvarez Gargallo",
    organizacion: "Planning & Commercial",
    rol: "Commercial & Planning Manager",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Criterios comerciales/costos y priorización",
    accionClave: "Revisión de costos/AFE",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-014",
    nombre: "Fabiola Guzman",
    organizacion: "Administración y Finanzas",
    rol: "Adm. & Finance Manager",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 3,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Imputación/finanzas y aprobaciones",
    accionClave: "Validar imputación",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-015",
    nombre: "German Chaves Narvaez",
    organizacion: "Desarrollo",
    rol: "Development Sr. Manager",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 3,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Alineación desarrollo y restricciones",
    accionClave: "Alinear restricciones",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-016",
    nombre: "Franklin Mauricio Garzon Henao",
    organizacion: "Operaciones - CPF",
    rol: "Production Lead Supervisor (Cliente directo)",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación operativa",
    accionClave: "Sync operativo",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-017",
    nombre: "Julian Zamora Erazo",
    organizacion: "Operaciones - PADS",
    rol: "Production Lead Engineer (Cliente directo)",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Soporte técnico Oper",
    accionClave: "Aclaraciones técnicas",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-018",
    nombre: "Daniel Guacheta Godoy",
    organizacion: "Operaciones - PADS",
    rol: "Production Sr. Supervisor (Cliente directo)",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación operativa",
    accionClave: "Sync operativo",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-019",
    nombre: "Eduar Condia Diaz",
    organizacion: "Operaciones - CPF",
    rol: "Production Lead Supervisor (Cliente directo)",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación operativa",
    accionClave: "Sync operativo",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-020",
    nombre: "Eduardo Fabian Zambrano Parra",
    organizacion: "Facilidades / Proyectos",
    rol: "Supervisor de Obras Mecánicas",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Seguimiento técnico",
    accionClave: "Sync técnico",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-021",
    nombre: "Franklin Alfonso Torres Moreno",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Lead Supervisor",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Civil/tierra/entregables",
    accionClave: "Sync técnico",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-022",
    nombre: "German Adolfo Bolaños Lenis",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Lead Supervisor",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Eléctrica",
    accionClave: "Sync técnico",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Quincenal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-023",
    nombre: "Samir Alberto Perez Cubillos",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Lead Supervisor",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Instrumentación y Control",
    accionClave: "Sync técnico",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-024",
    nombre: "Sebastian Laverde Mora",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Lead Supervisor",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Eléctrica",
    accionClave: "Sync técnico",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-025",
    nombre: "Jhylsson Rafael García Nuncira",
    organizacion: "Facilidades / Proyectos",
    rol: "Facilities Lead Supervisor",
    nivelOrganizacional: 3,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación FAC",
    accionClave: "Sync equipo",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Nivel 4-6 — Supervisores en campo, IT, Security
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-026",
    nombre: "Alexis Melendez Ocampo",
    organizacion: "Mantenimiento",
    rol: "Maintenance Manager (Cliente directo)",
    nivelOrganizacional: 4,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Disponibilidad/aceptación de mantenimientos",
    accionClave: "Revisión plan Mtto",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-027",
    nombre: "Jean Carlos Motta Osma",
    organizacion: "Operaciones - CPF",
    rol: "Production Sr. Supervisor (Cliente directo)",
    nivelOrganizacional: 4,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación operativa en sitio",
    accionClave: "Sync operativo",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-028",
    nombre: "Luis Sebastian Rojas Vela",
    organizacion: "Operaciones - CPF",
    rol: "Production Lead Engineer (Cliente directo)",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Soporte técnico Oper",
    accionClave: "Aclaraciones técnicas",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-029",
    nombre: "Jose Javier Barrera Paez",
    organizacion: "Security",
    rol: "Security Manager",
    nivelOrganizacional: 6,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Accesos y seguridad en campo",
    accionClave: "Plan accesos",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-030",
    nombre: "Gilberto Barreneche",
    organizacion: "Security",
    rol: "Security Manager",
    nivelOrganizacional: 6,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Accesos y seguridad en campo",
    accionClave: "Plan accesos",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-031",
    nombre: "Wilson Fernando Changoluisa Criollo",
    organizacion: "IT",
    rol: "IT Manager",
    nivelOrganizacional: 6,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Soporte TI (PMIS, accesos, licencias)",
    accionClave: "Mesa TI",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Reguladores y Gobierno
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-032",
    nombre: "ANLA",
    organizacion: "Regulador Ambiental",
    rol: "Autoridad de licenciamiento y seguimiento",
    nivelOrganizacional: 2,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Cumplimiento de licencias/PMAs, evidencias y atención de visitas",
    accionClave: "Plan de cumplimiento + carpeta de evidencias + simulacro de visita",
    responsableEmail: "catalina.echeverri@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-033",
    nombre: "ANH",
    organizacion: "Regulador Hidrocarburos",
    rol: "Fiscalización/telemetría y medición",
    nivelOrganizacional: 2,
    poder: 5,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Cumplimiento resoluciones ANH (telemetría, medición, reportes)",
    accionClave: "Mesa técnica cumplimiento + roadmap + cierre hallazgos",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-034",
    nombre: "Alcaldía Puerto Gaitán",
    organizacion: "Gobierno local",
    rol: "Alcaldía / permisos / articulación territorial",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Información oportuna, coordinación de socializaciones y permisos",
    accionClave: "Mesa territorial + actas + plan socialización",
    responsableEmail: "catalina.echeverri@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-035",
    nombre: "Alcaldía Puerto Triunfo",
    organizacion: "Gobierno local",
    rol: "Alcaldía / articulación territorial",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación territorial y comunicación",
    accionClave: "Mesa territorial + acuerdos",
    responsableEmail: "catalina.echeverri@tecpetrol.com",
    canalPreferido: "Reunión",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-036",
    nombre: "Servicio Público de Empleo (SPE)",
    organizacion: "Empleo / Gobierno",
    rol: "Publicación y control de vacantes",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Publicación de vacantes y trazabilidad de códigos",
    accionClave: "Registro vacantes + volante + seguimiento códigos",
    responsableEmail: "camilo.cabrejo@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Comunidades
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-037",
    nombre: "Comunidad Cuernavaca",
    organizacion: "Comunidad / Entorno",
    rol: "Asentamiento/relacionamiento social",
    nivelOrganizacional: 4,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Transparencia, empleo local, mitigación de impactos, no negociación predial por terceros",
    accionClave: "Socialización + gestión compromisos + canal quejas",
    responsableEmail: "catalina.echeverri@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-038",
    nombre: "Comunidades indígenas (área influencia)",
    organizacion: "Comunidad / Entorno",
    rol: "Autoridades y líderes indígenas",
    nivelOrganizacional: 4,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Participación y acuerdos; gestión de impactos",
    accionClave: "Ruta de relacionamiento + acuerdos + seguimiento",
    responsableEmail: "catalina.echeverri@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Economía local y vecinos operadoras
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-039",
    nombre: "Empresarios Puerto Gaitán",
    organizacion: "Economía local",
    rol: "Proveedores/servicios locales",
    nivelOrganizacional: 5,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Oportunidades de contratación transparente",
    accionClave: "Rueda de negocio + requisitos + calendario",
    responsableEmail: "nicolas.militello@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Trimestral",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-040",
    nombre: "Empresarios Puerto Triunfo",
    organizacion: "Economía local",
    rol: "Proveedores/servicios locales",
    nivelOrganizacional: 5,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Oportunidades de contratación",
    accionClave: "Rueda de negocio + requisitos",
    responsableEmail: "nicolas.militello@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Trimestral",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-041",
    nombre: "Ecopetrol",
    organizacion: "Operadora / Vecino",
    rol: "Interfaz operativa/ambiental (áreas compartidas)",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 3,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación de interfaces y permisos",
    accionClave: "Mesa de interfaces + acuerdos",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-042",
    nombre: "Frontera",
    organizacion: "Operadora / Vecino",
    rol: "Interfaz operativa/servicios compartidos",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 2,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinación si hay interfaces",
    accionClave: "Contacto puntual + matriz interfaces",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-043",
    nombre: "Otras operadoras",
    organizacion: "Operadora / Vecino",
    rol: "Interfaz operativa/servicios compartidos",
    nivelOrganizacional: 5,
    poder: 3,
    interes: 2,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Coordinar cruces e interferencias",
    accionClave: "Matriz interfaces + comunicaciones",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Contratistas y servicios
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-044",
    nombre: "Proveedores cantera regional",
    organizacion: "Suministros / Cantera",
    rol: "Material pétreo + logística",
    nivelOrganizacional: 5,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Programación de despachos, calidad y costos",
    accionClave: "Contrato/OS + plan logística + control calidad",
    responsableEmail: "franklin.torres@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-045",
    nombre: "Contratistas de O&M",
    organizacion: "Operación y Mantenimiento",
    rol: "Ejecución O&M / programación",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Ventanas operativas, permisos, recursos",
    accionClave: "Programación semanal + gestión avisos + comité",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-046",
    nombre: "Techint",
    organizacion: "Contratista",
    rol: "Ingeniería, construcción y O&M",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Definiciones técnicas, LDS, control documental",
    accionClave: "Revisión semanal + transmittals + decisiones",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-047",
    nombre: "Contratistas de Ingeniería",
    organizacion: "Contratista",
    rol: "Ingeniería y control documental",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Alcance claro, tiempos de revisión y feedback",
    accionClave: "Cadencia de revisiones + tablero de entregables",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-048",
    nombre: "Contratistas marco",
    organizacion: "Contratista",
    rol: "Obras por disciplina",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Accesos, pagos, materiales, lineamientos HSE",
    accionClave: "KOM + comité obra + matriz de compromisos",
    responsableEmail: "team.fac@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-049",
    nombre: "Prestadores hotel y alimentación",
    organizacion: "Servicios locales",
    rol: "Hospedaje y alimentación",
    nivelOrganizacional: 5,
    poder: 2,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Pronóstico de demanda, pagos y estándares HSE",
    accionClave: "Acuerdo de servicio + KPI calidad + calendarización",
    responsableEmail: "administracion@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Mensual",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  // ───────────────────────────────────────────────────────────────────
  // Proveedores especialistas y OT
  // ───────────────────────────────────────────────────────────────────
  {
    codigo: "STK-050",
    nombre: "Schneider",
    organizacion: "Proveedor especialista",
    rol: "SCADA/Control/FAT/SAT",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Plan FAT/SAT, accesos y documentación",
    accionClave: "Plan FAT + lista personal + agenda pruebas",
    responsableEmail: "samir.perez@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Por hito",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-051",
    nombre: "Flowserve",
    organizacion: "Proveedor especialista",
    rol: "Bombas/repumping y soporte",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Curvas, soporte técnico y tiempos de respuesta",
    accionClave: "Mesa técnica + minutas + cierre pendientes",
    responsableEmail: "rafael.garcia@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Quincenal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-052",
    nombre: "SYZ",
    organizacion: "Proveedor especialista",
    rol: "Medición ultrasónica y comisionamiento",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 4,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Entrega equipos, procedimientos, permisos de ingreso",
    accionClave: "Seguimiento entregas + ingreso Parservicios + PDT servicio",
    responsableEmail: "daniel.medina@tecpetrol.com",
    canalPreferido: "Correo",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-053",
    nombre: "IT/OT Tecpetrol",
    organizacion: "Sistemas",
    rol: "Soporte TI/OT, accesos, integración",
    nivelOrganizacional: 4,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Arquitectura, accesos, ciberseguridad, continuidad",
    accionClave: "Mesa IT/OT + backlog + decisiones de arquitectura",
    responsableEmail: "wilson.changoluisa@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-054",
    nombre: "CARMONA Leandro Jose",
    organizacion: "Sistemas (OT)",
    rol: "Operation Technology Manager",
    nivelOrganizacional: 3,
    poder: 4,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Arquitectura OT/SCADA y telemetría eléctrica",
    accionClave: "Definir arquitectura + roadmap integración + priorización",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Quincenal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
  {
    codigo: "STK-055",
    nombre: "PAZANIN Raiquen Nehuen",
    organizacion: "Sistemas (OT)",
    rol: "Operations Technology Expert",
    nivelOrganizacional: 4,
    poder: 3,
    interes: 5,
    engagementActual: "NEUTRAL",
    engagementDeseado: "APOYA",
    necesidad: "Soporte técnico OT (SCADA, lógicas, FAT)",
    accionClave: "Revisión técnica + validación lógicas + soporte FAT",
    responsableEmail: "nestor.contreras@tecpetrol.com",
    canalPreferido: "Mixto",
    cadencia: "Semanal",
    sentimientoIa: "NEUTRO",
    proximaRevision: "2026-06-13",
    estado: "ACTIVO",
  },
];

/**
 * Asegura que los 55 stakeholders canónicos estén cargados.
 * Idempotente: solo siembra si la tabla está vacía para el tenant.
 *
 * El campo `codigo` del seed es informativo; el repo asigna el código
 * secuencial real (STK-001, STK-002, …). Como sembramos en orden, los
 * códigos coinciden 1:1 con el Excel.
 *
 * @returns número de stakeholders creados (0 si ya había datos).
 */
export async function ensureSeedStakeholders(
  tenantId: string,
  userEmail: string = "system@seed",
): Promise<number> {
  // Modo operación → seeds bloqueados. Ver `core/data/modoOperacion.ts`.
  const { estaEnModoOperacion } = await import("@core/data/modoOperacion");
  if (estaEnModoOperacion()) return 0;
  const repo = buildRepos(tenantId).stakeholder;
  const existentes = await repo.list();
  if (existentes.length > 0) return 0;

  let creados = 0;
  for (const spec of STAKEHOLDERS_FAC) {
    const { codigo: _codigo, sentimientoIa, ...resto } = spec;
    await repo.create(
      {
        ...resto,
        ...(sentimientoIa !== undefined && { sentimientoIa }),
        pinneadoAfeIds: [],
        // Fase 3 del ADR — los stakeholders seed pertenecen a la gerencia
        // natural (CO). Cada gerencia tiene su propia matriz; no admiten
        // sharing flag.
        gerenciaId: GERENCIA_DEFAULT,
      },
      userEmail,
    );
    creados++;
  }
  return creados;
}
